import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ARCInfo } from "arcjs/common/arc_devices";
import { ARCStatus, Fleet } from "types/fleet";
import { ArcsContext, ArcsContextType } from "./FleetProvider";
import {
    getArcs,
    getProxy,
    login,
    renameArc as renameArcRequest,
    setArcThumbnail as setArcThumbnailRequest,
} from "lib/orgServer/OrgServerClient";
import { getOrgId, getOrganizations, setOrgId } from "lib/config";
import { useBridgeProbe } from "lib/ros/useBridgeProbe";
import { useArcNetworkStatus } from "lib/ros/useArcNetworkStatus";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import { DefaultControlSource } from "lib/consts/ARC/connection";
import { ARC_GATEWAY, arcPathPrefix } from "lib/consts/arcGateway";
import Portal from "components/layout/Portal";
import OrganizationMenu from "components/layout/navbar/OrganizationMenu";

const OrgServerLoginModal = ({ visible, onSuccess }: { visible: boolean; onSuccess: () => void }) => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [organizationId, setOrganizationId] = useState(() => getOrgId() ?? "");
    const [organizations] = useState<string[]>(() => getOrganizations());
    // Type a new organization id when there are no previously used ones.
    const [editingOrgId, setEditingOrgId] = useState(() => organizations.length === 0);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!organizationId.trim()) {
            setError("Organization ID is required");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setOrgId(organizationId.trim());
            await login(username, password);
            onSuccess();
        } catch {
            setError("Invalid credentials");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatedModal
            visible={visible}
            onClose={() => { }}
            closeOnBackdrop={false}
            closeOnEscape={false}
            className="w-full max-w-sm rounded-r10 bg-black80 p-6"
        >
            <h2 className="text-h4 font-medium text-white mb-4">Sign in to your fleet</h2>
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                {editingOrgId ? (
                    <div className="flex flex-col gap-1">
                        <input
                            type="text"
                            placeholder="Organization ID"
                            value={organizationId}
                            onChange={(e) => setOrganizationId(e.target.value)}
                            className="w-full bg-black70 border border-black80 rounded p-2 text-h5 text-white"
                            autoFocus
                        />
                        {organizations.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setEditingOrgId(false)}
                                className="self-start text-h5 text-primary2 hover:underline"
                            >
                                use existing organization
                            </button>
                        )}
                    </div>
                ) : (
                    <OrganizationMenu
                        className="w-full"
                        currentOrg={organizationId || null}
                        organizations={organizations}
                        onPickOrg={(org) => setOrganizationId(org)}
                        footerLabel="New organization"
                        onFooter={() => {
                            setOrganizationId("");
                            setEditingOrgId(true);
                        }}
                        buttonClassName="w-full justify-between bg-black70 border border-black80 rounded p-2 text-h5 text-white"
                    />
                )}
                <input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full bg-black70 border border-black80 rounded p-2 text-h5 text-white"
                    autoFocus={!editingOrgId}
                />
                <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-black70 border border-black80 rounded p-2 text-h5 text-white"
                />
                {error && <p className="text-h5 text-red-400">{error}</p>}
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2 bg-primary2 text-white rounded text-h5 disabled:opacity-50"
                >
                    {loading ? "Signing in…" : "Sign in"}
                </button>
            </form>
        </AnimatedModal>
    );
};

type OrgFleetContextType = ArcsContextType & {
    proxyHostname: string | null;
    renameArc: (serialNumber: string, name: string) => Promise<void>;
    /** Per-serial thumbnail hash (see useArcThumbnail); null when none was uploaded. */
    thumbnailHashes: Record<string, string | null>;
    setArcThumbnail: (serialNumber: string, thumbnail: string | null) => Promise<void>;
};

const OrgFleetProvider = ({ children }: { children?: React.ReactNode }) => {
    const [arcs, setArcs] = useState<ARCInfo[]>([]);
    const [proxyHostname, setProxyHostname] = useState<string | null>(null);
    const [needsLogin, setNeedsLogin] = useState(false);
    const [thumbnailHashes, setThumbnailHashes] = useState<Record<string, string | null>>({});

    const fetchData = useCallback(async () => {
        try {
            const [orgArcs, proxy] = await Promise.all([getArcs(), getProxy()]);
            const proxyHost = proxy?.publicHostname?.trim() || "";
            setArcs(orgArcs.map((a) => ({
                serialNumber: a.serialNumber,
                name: a.name,
                // Fleet browser traffic always uses the organization's public
                // proxy origin. The ARC Tailscale IP remains server-side data;
                // it must never become a browser fallback.
                host: proxyHost,
                // Keep an unassigned ARC visible but inert rather than letting
                // the multi-ARC provider construct a socket with an empty host.
                controlSource: proxyHost ? DefaultControlSource : "DUMMY",
                controlSourceOptions: {
                    fleetArcId: a.serialNumber,
                    ...(proxyHost
                        ? {
                              useSSL: true,
                              port: null,
                              subpath: `${arcPathPrefix(a.serialNumber)}${ARC_GATEWAY.ros}`.replace(/^\//, ""),
                        }
                        : {}),
                },
                robot: { serialNumber: a.serialNumber, name: a.name, type: "spot" },
                payload: "default",
            })));
            setProxyHostname(proxyHost || null);
            setNeedsLogin(false);
            // Kept apart from ARCInfo so a thumbnail change doesn't touch the
            // ARC list the connection providers depend on.
            setThumbnailHashes(Object.fromEntries(orgArcs.map((a) => [a.serialNumber, a.thumbnailHash])));
        } catch (err: any) {
            const msg = err?.message ?? "";
            if (msg.includes("401") || msg.includes("Missing token") || msg.includes("Unauthorized")) {
                setNeedsLogin(true);
            }
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const fleets: Fleet[] = useMemo(() => [{
        id: "org-fleet",
        fleetName: "My Fleet",
        arcs,
    } as Fleet], [arcs]);

    const getFleetById = useCallback(
        (id: string) => fleets.find((f) => f.id === id),
        [fleets]
    );

    const serialNumbers = useMemo(() => arcs.map((a) => a.serialNumber), [arcs]);
    const { onlineMap, batteryMap } = useBridgeProbe(proxyHostname, serialNumbers);
    const networkMap = useArcNetworkStatus(proxyHostname, serialNumbers);

    const arcStatuses = useMemo(
        () =>
            Object.fromEntries(
                serialNumbers.map((sn) => [
                    sn,
                    {
                        online: onlineMap[sn] ?? false,
                        batteryLevel: batteryMap[sn],
                        networks: networkMap[sn] ?? [],
                    } as Partial<ARCStatus>,
                ])
            ),
        [serialNumbers, onlineMap, batteryMap, networkMap]
    );

    // Optimistic: the card shows the new name immediately and reverts if org_server rejects it.
    const renameArc = useCallback(async (serialNumber: string, name: string) => {
        const setName = (value: string) =>
            setArcs((prev) =>
                prev.map((a) =>
                    a.serialNumber === serialNumber
                        ? { ...a, name: value, robot: { ...a.robot, name: value } }
                        : a
                )
            );
        const previous = arcs.find((a) => a.serialNumber === serialNumber)?.name ?? serialNumber;
        setName(name.trim() || serialNumber);
        try {
            const updated = await renameArcRequest(serialNumber, name);
            setName(updated.name);
        } catch (err) {
            setName(previous);
            throw err;
        }
    }, [arcs]);

    // Not optimistic: the new hash only exists once org_server has stored the
    // image. ARCCard shows the picked image locally until then.
    const setArcThumbnail = useCallback(async (serialNumber: string, thumbnail: string | null) => {
        const hash = await setArcThumbnailRequest(serialNumber, thumbnail);
        setThumbnailHashes((prev) => ({ ...prev, [serialNumber]: hash }));
    }, []);

    const contextValue: OrgFleetContextType = {
        fleetRepo: null as any,
        arcsInfo: arcs,
        fleets,
        getFleetById,
        arcStatuses,
        proxyHostname,
        renameArc,
        thumbnailHashes,
        setArcThumbnail,
    };

    return (
        <>
            <OrgServerLoginModal visible={needsLogin} onSuccess={fetchData} />
            <ArcsContext.Provider value={contextValue}>
                {children}
            </ArcsContext.Provider>
        </>
    );
};

export const useOrgFleet = () => useContext(ArcsContext) as OrgFleetContextType;

export default OrgFleetProvider;
